import React, {
    useCallback,
    useEffect,
    useState
} from "react";

import axios from "axios";

import Appconfig from "../../config/config";
import { useSelector } from "react-redux";

import "./WelcomeBannerModal.css";
import { useLocation } from "react-router-dom";

function WelcomeBannerModal() {

    const storedUserInfo = useSelector(
        (state) => state?.userInfo?.userdata
    );

    const userInfo =
        storedUserInfo?.value || storedUserInfo || null;

    const location = useLocation();

    const isLoggedIn = Boolean(userInfo?._id);

    const isDashboardPage = location.pathname === "/";


    const siteCode = String(
        userInfo?.site_code ||
        Appconfig.sitecodes ||
        ""
    ).trim();

    const loginSessionId =
        window.localStorage.getItem("loginTime") || "no-login";

    const welcomeBannerSessionKey = `welcome-banner-seen-${userInfo?._id || "guest"
        }-${siteCode}-${loginSessionId}`;

    const apiBaseUrl = String(
        Appconfig.apiUrl || ""
    ).replace(/\/+$/, "");

    const [banners, setBanners] = useState([]);
    const [activeIndex, setActiveIndex] =
        useState(0);

    const [isOpen, setIsOpen] =
        useState(false);

    const [loading, setLoading] =
        useState(false);

    const currentBanner =
        banners[activeIndex] || null;

    const getImageUrl = useCallback(
        (imagePath) => {
            if (!imagePath) {
                return "";
            }

            if (
                imagePath.startsWith("http://") ||
                imagePath.startsWith("https://")
            ) {
                return imagePath;
            }

            return `${apiBaseUrl}${imagePath.startsWith("/") ? "" : "/"
                }${imagePath}`;
        },
        [apiBaseUrl]
    );

    const closeModal = useCallback(() => {
        setIsOpen(false);
    }, []);

    const showNextBanner = useCallback(() => {
        setActiveIndex((previousIndex) => {
            if (banners.length <= 1) {
                return 0;
            }

            return (
                (previousIndex + 1) %
                banners.length
            );
        });
    }, [banners.length]);

    useEffect(() => {
        let componentActive = true;

        const fetchWelcomeBanners = async () => {
            if (
                !isLoggedIn ||
                !isDashboardPage ||
                !siteCode
            ) {
                setBanners([]);
                setIsOpen(false);
                return;
            }

            if (
                window.sessionStorage.getItem(
                    welcomeBannerSessionKey
                ) === "shown"
            ) {
                setBanners([]);
                setIsOpen(false);
                return;
            }

            setLoading(true);

            try {
                const response = await axios.post(
                    `${apiBaseUrl}/welcomeBanner/active`,
                    {
                        site_code: siteCode
                    },
                    {
                        headers: {
                            "Content-Type":
                                "application/json"
                        }
                    }
                );

                if (!componentActive) {
                    return;
                }

                const activeBanners =
                    response.data?.result === 1 &&
                        Array.isArray(
                            response.data.resultData
                        )
                        ? response.data.resultData
                        : [];

                setBanners(activeBanners);
                setActiveIndex(0);

                if (activeBanners.length > 0) {
                    window.sessionStorage.setItem(
                        welcomeBannerSessionKey,
                        "shown"
                    );

                    setIsOpen(true);
                }
            } catch (error) {
                console.error(
                    "WELCOME BANNER FETCH ERROR:",
                    error
                );

                if (componentActive) {
                    setBanners([]);
                    setIsOpen(false);
                }
            } finally {
                if (componentActive) {
                    setLoading(false);
                }
            }
        };

        fetchWelcomeBanners();

        return () => {
            componentActive = false;
        };
    }, [
        apiBaseUrl,
        siteCode,
        isLoggedIn,
        isDashboardPage,
        welcomeBannerSessionKey
    ]);

    useEffect(() => {
        banners.forEach((banner) => {
            const image = new Image();

            image.src = getImageUrl(
                banner.image_url
            );
        });
    }, [banners, getImageUrl]);

    /*
     * Current banner ke admin-defined duration
     * ke baad next banner automatically show hoga.
     */
    useEffect(() => {
        if (
            !isOpen ||
            banners.length <= 1 ||
            !currentBanner
        ) {
            return undefined;
        }

        const durationInSeconds = Math.max(
            1,
            Number(
                currentBanner.display_duration
            ) || 5
        );

        const timer = window.setTimeout(
            showNextBanner,
            durationInSeconds * 1000
        );

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        activeIndex,
        banners.length,
        currentBanner,
        isOpen,
        showNextBanner
    ]);

    /*
     * Modal open ho to background page scroll
     * temporarily disable hoga.
     */
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow = "hidden";

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                closeModal();
            }
        };

        document.addEventListener(
            "keydown",
            handleEscape
        );

        return () => {
            document.body.style.overflow =
                previousOverflow;

            document.removeEventListener(
                "keydown",
                handleEscape
            );
        };
    }, [isOpen, closeModal]);

    if (
        loading ||
        !isOpen ||
        !currentBanner
    ) {
        return null;
    }

    return (
        <div
            className="welcome-popup-overlay"
            role="presentation"
        >
            <section
                className="welcome-popup"
                role="dialog"
                aria-modal="true"
                aria-label="Welcome banner"
            >
                <header className="welcome-popup-header">
                    <div className="welcome-popup-title">
                        <span aria-hidden="true">
                            ⚠️
                        </span>

                        <strong>
                            {currentBanner.title ||
                                "Welcome"}
                        </strong>
                    </div>

                    <button
                        type="button"
                        className="welcome-popup-close"
                        onClick={closeModal}
                        aria-label="Close welcome banner"
                    >
                        ×
                    </button>
                </header>

                <div className="welcome-popup-image-wrap">
                    <img
                        className="welcome-popup-image"
                        src={getImageUrl(
                            currentBanner.image_url
                        )}
                        alt={
                            currentBanner.title ||
                            "Welcome banner"
                        }
                    />
                </div>
            </section>
        </div>
    );
}

export default WelcomeBannerModal;